import { RATINGS, getStoreUrl } from './storeLinks'

export type GatedPlatform = 'ios' | 'android'

/** Cookie set by the "Continue on the Web" escape hatch on the hard gate. */
export const MOBILE_WEB_COOKIE = 'baba_mobile_web'

export function detectGatedPlatform(ua: string | null | undefined): GatedPlatform | null {
  if (!ua) return null
  if (/iPhone|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return 'android' // Android tablets omit "Mobile"
  return null
}

/**
 * True once a phone visitor has clicked "Continue on the Web" on the hard
 * gate. Middleware treats this the same as `MOBILE_GATE !== 'hard'` — the
 * cookie is the visitor's own override, not a config change.
 */
export function hasContinuedOnWeb(cookieHeader: string | null | undefined): boolean {
  if (!cookieHeader) return false
  return cookieHeader.split(';').some((pair) => {
    const eq = pair.indexOf('=')
    if (eq === -1) return false
    return pair.slice(0, eq).trim() === MOBILE_WEB_COOKIE && pair.slice(eq + 1).trim() === '1'
  })
}

// Apple / Google Play glyph paths — same paths as src/components/StoreBadges.tsx.
const APPLE_SVG =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>'
const PLAY_SVG =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z"/></svg>'

function storeMeta(platform: GatedPlatform): { icon: string; sub: string; label: string } {
  return platform === 'ios'
    ? { icon: APPLE_SVG, sub: 'Download on the', label: 'App Store' }
    : { icon: PLAY_SVG, sub: 'Get it on', label: 'Google Play' }
}

/**
 * Full-screen interstitial served to phone user agents when `MOBILE_GATE`
 * is `'hard'`. Styled to match the Workspace design system (IBM Plex Sans,
 * 6px radii, --cream/--ink light + dark tokens via prefers-color-scheme —
 * this is a standalone edge-rendered page, so it can't read the app's .dark
 * class and switches on the media query instead).
 */
export function gateHtml(platform: GatedPlatform): string {
  const store = storeMeta(platform)
  // The gate only renders for a phone of this platform, so it is the device.
  const href = getStoreUrl(platform, 'mobile_gate', platform)
  const rating = RATINGS[platform]
  const ratingLine = `★ ${rating.score} · ${rating.count.toLocaleString()} ratings`

  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="robots" content="noindex"><title>baba lives in the app</title><style>
:root{--cream:#F3EFE7;--card:#FCFAF5;--ink:#16130F;--muted:#5A524A;--muted-2:#6E665B;--line:#D3C9B6;--accent-deep:#A85A38;--cta-fg:#FFFFFF}
@media (prefers-color-scheme:dark){:root{--cream:#121110;--card:#1B1917;--ink:#F1ECE3;--muted:#B8AFA1;--muted-2:#978E82;--line:rgba(241,236,227,.13);--accent-deep:#D78F4F;--cta-fg:#121110}}
*{box-sizing:border-box}
body{margin:0;font-family:'IBM Plex Sans',-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif;background:var(--cream);color:var(--ink);display:flex;min-height:100svh;align-items:center;justify-content:center;text-align:center}
main{padding:32px 28px;max-width:420px;width:100%}
img{width:84px;height:84px;border-radius:20px;box-shadow:0 12px 32px -12px rgba(22,19,15,.35)}
h1{font-size:26px;font-weight:600;letter-spacing:-.01em;line-height:1.2;margin:22px 0 10px}
p.lede{color:var(--muted);font-size:15px;line-height:1.5;margin:0 0 26px}
a.cta{display:inline-flex;align-items:center;justify-content:center;gap:10px;width:100%;height:52px;border-radius:6px;background:var(--ink);color:var(--cream);font-weight:600;font-size:15px;text-decoration:none}
a.cta .label{display:flex;flex-direction:column;align-items:flex-start;line-height:1.15}
a.cta .label .sub{font-size:10.5px;font-weight:500;opacity:.75}
p.rating{font-family:'IBM Plex Mono','SF Mono',Menlo,monospace;font-size:11px;color:var(--muted-2);margin:12px 0 0}
p.sub{margin-top:18px;font-size:12.5px;line-height:1.5;color:var(--muted-2)}
a.continue{display:inline-block;margin-top:26px;padding-top:14px;border-top:1px solid var(--line);font-size:13px;color:var(--muted);text-decoration:underline;text-underline-offset:2px}
</style></head><body><main>
<img src="/apple-touch-icon.png" alt="baba">
<h1>baba Lives in the App on Your Phone</h1>
<p class="lede">The web workspace is built for a big screen. The app is built for your pocket: voice, camera, slang, and gender-aware Hebrew, free.</p>
<a class="cta" href="${href}">${store.icon}<span class="label"><span class="sub">${store.sub}</span><span>${store.label}</span></span></a>
<p class="rating">${ratingLine}</p>
<p class="sub">Subscribed on the web? Your plan works in the app. Sign in with the same email.</p>
<p class="sub">On a computer? Open <strong>app.itsbaba.com</strong> there.</p>
<a class="continue" href="#" onclick="document.cookie='${MOBILE_WEB_COOKIE}=1; path=/; max-age=31536000; samesite=lax'; location.reload(); return false;">Continue on the Web</a>
</main></body></html>`
}
